import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  ACCESS_DURATION_DAYS,
  ACCESS_REQUEST_RESOURCE_NAME_MAX,
  ACCESS_RESOURCE_TYPE_GROUPS,
  ACCESS_RESOURCE_TYPE_LABELS,
  ACCESS_RESOURCE_TYPE_PROVIDERS,
  ACCESS_RISK_TIER_BLURB,
  riskTier,
  type AccessDurationKind,
  type AccessResourceType,
} from '@infras/shared-types';
import { useMe } from '../lib/useMe';
import { ACCESS_REVIEWERS, byLayer } from '../lib/access';
import type { AccessReviewer } from '../lib/access';
import { RichEditor } from '../components/RichEditor';

/**
 * New access request. Four questions: which system, which one (optional), how
 * long, and why.
 *
 * It used to ask six things — resource type, name, maintainer, reason, every
 * admin-configured question for that type, and optional extra reviewers — and
 * still produced an open-ended grant. The questions templates are retired from
 * this form (the API and their admin page stay, so historical answers keep
 * rendering) because nobody could answer them better than the reason field, and
 * they were the main reason people filed on Slack instead.
 *
 * Prefill from resource pages via ?type=repo&entityId=42&name=group/proj.
 * new_member: free-text only (the API refuses entity links for them).
 */
const CREATE = gql`
  mutation CreateAccessRequest($input: CreateAccessRequestInput!) {
    createAccessRequest(input: $input) {
      id
    }
  }
`;

const DAY_LABEL = (d: number) =>
  d === 365 ? '1 year' : d === 730 ? '2 years' : d === 1 ? '1 day' : `${d} days`;

export function AccessRequestNewPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { me } = useMe();
  const isNewMember = me?.role === 'new_member';

  const prefillType = params.get('type');
  const prefillEntityId = params.get('entityId');
  const prefillName = params.get('name');
  // Entity link only when fully prefilled AND the caller may link inventory.
  const linked = !isNewMember && !!prefillEntityId && !!prefillType && !!prefillName;

  const [resourceType, setResourceType] = useState<AccessResourceType>(
    (prefillType as AccessResourceType) || 'database',
  );
  const [resourceName, setResourceName] = useState(prefillName ?? '');
  const [durationKind, setDurationKind] = useState<AccessDurationKind>('permanent');
  const [durationDays, setDurationDays] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [mutErr, setMutErr] = useState<string | null>(null);

  const { data: revData } = useQuery<{ accessReviewers: AccessReviewer[] }>(ACCESS_REVIEWERS);
  const reviewers = revData?.accessReviewers ?? [];
  const layers = byLayer(reviewers);

  const [create, { loading: creating }] = useMutation(CREATE);

  const tier = riskTier(resourceType, durationKind);
  const nameTooLong = !linked && resourceName.trim().length > ACCESS_REQUEST_RESOURCE_NAME_MAX;
  // Reason is now REQUIRED: with the questions gone and the name optional, it is
  // the only place the reviewer learns what is actually being asked for.
  const canSubmit =
    !creating &&
    !!reason.trim() &&
    !nameTooLong &&
    (durationKind === 'permanent' || durationDays !== null);

  const submit = async () => {
    setMutErr(null);
    try {
      const res = await create({
        variables: {
          input: {
            resourceType,
            entityId: linked ? Number(prefillEntityId) : null,
            resourceName: linked ? null : resourceName.trim() || null,
            reason: reason.trim(),
            durationKind,
            durationDays: durationKind === 'temporary' ? durationDays : null,
          },
        },
      });
      const id = (res.data as { createAccessRequest?: { id: string } } | null)?.createAccessRequest
        ?.id;
      if (id) navigate(`/access/${id}`);
    } catch (e) {
      setMutErr(e instanceof Error ? e.message : 'Could not file the request');
    }
  };

  const provider = ACCESS_RESOURCE_TYPE_PROVIDERS[resourceType];

  return (
    <div className="page access-form-page">
      <div className="detail-back">
        <Link to="/access" className="muted">
          ← Access requests
        </Link>
      </div>
      <div className="detail-head">
        <h2>Request access</h2>
      </div>

      <div className="req-form-grid">
        <div className="stack">
          <div className="access-field">
            <label htmlFor="req-system">Which system?</label>
            <select
              id="req-system"
              className="class-select"
              value={resourceType}
              disabled={linked}
              onChange={(e) => setResourceType(e.target.value as AccessResourceType)}
            >
              {ACCESS_RESOURCE_TYPE_GROUPS.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.types.map((t) => (
                    <option key={t} value={t}>
                      {ACCESS_RESOURCE_TYPE_LABELS[t]}
                      {ACCESS_RESOURCE_TYPE_PROVIDERS[t] ? ` — ${ACCESS_RESOURCE_TYPE_PROVIDERS[t]}` : ''}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          <div className="access-field">
            <label htmlFor="req-name">
              Which one? <span className="muted">optional</span>{' '}
              {linked && <span className="tag-badge">linked to inventory</span>}
            </label>
            {linked ? (
              // A resource name renders in Plex Sans — phase 6 §1 mono policy.
              <p>{prefillName}</p>
            ) : (
              <>
                <input
                  id="req-name"
                  value={resourceName}
                  placeholder="e.g. signagecloud-qa, grafana-v2.opstech.org"
                  onChange={(e) => setResourceName(e.target.value)}
                />
                <p className="muted text-sm">
                  Leave blank if the reason below already says it. Defaults to
                  &ldquo;{ACCESS_RESOURCE_TYPE_LABELS[resourceType]}&rdquo;.
                </p>
                {nameTooLong && (
                  <p className="error">
                    Keep it under {ACCESS_REQUEST_RESOURCE_NAME_MAX} characters.
                  </p>
                )}
              </>
            )}
          </div>

          <div className="access-field">
            <label>How long?</label>
            <div className="view-toggle">
              <button
                type="button"
                className={durationKind === 'permanent' ? 'active' : ''}
                onClick={() => {
                  setDurationKind('permanent');
                  setDurationDays(null);
                }}
              >
                Permanent
              </button>
              <button
                type="button"
                className={durationKind === 'temporary' ? 'active' : ''}
                onClick={() => setDurationKind('temporary')}
              >
                Temporary
              </button>
            </div>
            {durationKind === 'temporary' && (
              <div className="day-chips">
                {ACCESS_DURATION_DAYS.map((d) => (
                  <button
                    key={d}
                    type="button"
                    className={`mini-btn${durationDays === d ? ' mini-btn--on' : ''}`}
                    onClick={() => setDurationDays(d)}
                  >
                    {DAY_LABEL(d)}
                  </button>
                ))}
              </div>
            )}
            <p className="muted text-sm">
              {durationKind === 'permanent'
                ? 'Ongoing access, with no end date.'
                : durationDays
                  ? `Expires ${new Date(Date.now() + durationDays * 86_400_000).toLocaleDateString()}.`
                  : 'Pick how long you need it.'}
            </p>
          </div>

          <div className="access-field">
            <label>Why do you need it?</label>
            <RichEditor
              value={reason}
              onChange={setReason}
              placeholder="What are you trying to do, and which exact resource? Specific reasons are approved faster."
              disabled={creating}
            />
          </div>

          {mutErr && <p className="error">{mutErr}</p>}

          <div className="btn-row">
            <button className="btn btn-primary" disabled={!canSubmit} onClick={() => void submit()}>
              {creating ? 'Filing…' : 'File request'}
            </button>
            <Link to="/access" className="btn btn-ghost">
              Cancel
            </Link>
          </div>
        </div>

        {/* Live summary — replaces a static "How access requests work" list that
            said nothing about the request being filed. */}
        <aside className="stack-sm">
          <h3 className="panel-title">Request summary</h3>
          <div className="attrs">
            <div className="attrs__row">
              <div className="attrs__k">System</div>
              <div className="attrs__v">
                {ACCESS_RESOURCE_TYPE_LABELS[resourceType]}
                {provider && <span className="muted"> · {provider}</span>}
              </div>
            </div>
            <div className="attrs__row">
              <div className="attrs__k">Target</div>
              <div className="attrs__v">
                {linked ? prefillName : resourceName.trim() || ACCESS_RESOURCE_TYPE_LABELS[resourceType]}
              </div>
            </div>
            <div className="attrs__row">
              <div className="attrs__k">Duration</div>
              <div className="attrs__v">
                {durationKind === 'permanent'
                  ? 'Permanent'
                  : durationDays
                    ? `${DAY_LABEL(durationDays)} — expires ${new Date(
                        Date.now() + durationDays * 86_400_000,
                      ).toLocaleDateString()}`
                    : <span className="muted">not chosen yet</span>}
              </div>
            </div>
            <div className="attrs__row">
              <div className="attrs__k">Review path</div>
              <div className="attrs__v">
                <span className={`risk-badge risk-badge--${tier}`}>{tier}</span>
                <div className="muted text-sm" style={{ marginTop: 4 }}>
                  {ACCESS_RISK_TIER_BLURB[tier]}
                </div>
              </div>
            </div>
          </div>

          {/* Fed by accessReviewers, so this can't go stale the way prose would. */}
          <h3 className="panel-title">Reviewers</h3>
          {layers.length === 0 ? (
            <p className="muted text-sm">No reviewers configured yet.</p>
          ) : (
            <div className="attrs">
              {layers.map(([layer, list]) => (
                <div className="attrs__row" key={layer}>
                  <div className="attrs__k">
                    Layer {layer} {layer === 1 && <span className="muted">— assigned on submit</span>}
                  </div>
                  <div className="attrs__v">{list.map((r) => r.email).join(', ')}</div>
                </div>
              ))}
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
